/**
 * Canonical dashboard energy forecast view model.
 * Single source of truth for sunrise/overnight forecast values consumed by:
 * - EnergyOutlook (chart + scenario cards)
 * - OvernightForecastCard (headline kWh at sunrise)
 * - EnergyGuidanceCard (preservation/permission decisions + displayed values)
 *
 * Eliminates inconsistencies caused by different components reading different backend sources.
 */

import type { ForecastData } from '@/types/dashboard';
import type { OvernightForecastResult } from '@/hooks/useOvernightForecast';
import type { EnergyGuidanceResult } from '@/hooks/useEnergyGuidance';

// ─── Public types ────────────────────────────────────────────────────────────

export type ForecastSource = 'forecast-runway' | 'overnight_forecasts' | 'unavailable';

export interface DashboardForecastViewModel {
  /** Which backend source is providing the sunrise values. */
  source: ForecastSource;
  /** Current measured battery energy (kWh). */
  currentEnergyKwh: number;
  /** Reserve threshold (kWh). Battery should not go below this. */
  reserveThresholdKwh: number;
  /** Total expected kWh at sunrise (includes reserve). */
  expectedAtSunriseKwh: number;
  /** Best-case total kWh at sunrise. */
  bestAtSunriseKwh: number;
  /** Worst-case total kWh at sunrise. */
  worstAtSunriseKwh: number;
  /** Usable above reserve: max(0, expected - reserve). */
  expectedUsableAtSunriseKwh: number;
  /** Usable above reserve: max(0, best - reserve). */
  bestUsableAtSunriseKwh: number;
  /** Usable above reserve: max(0, worst - reserve). */
  worstUsableAtSunriseKwh: number;
  /** Whether the worst-case scenario breaches reserve before sunrise. */
  reachesReserveBeforeSunrise: boolean;
  /** Time-to-reserve string (e.g. "3h 20m") if available. */
  timeToReserve: string | null;
  /** Confidence from the active source. */
  confidence: 'low' | 'medium' | 'high';
  /** Whether the forecast data is considered fresh/valid. */
  isFresh: boolean;
}

// ─── Builder inputs ──────────────────────────────────────────────────────────

export interface BuildForecastViewModelInput {
  /** forecast-runway data (preferred source). */
  forecastRunway: ForecastData | null;
  forecastRunwayLoading: boolean;
  forecastRunwayError: string | null;
  /** overnight_forecasts table data (fallback source). */
  overnightForecast: OvernightForecastResult | null;
  /** Energy guidance backend result (for checking consistency). */
  energyGuidance: EnergyGuidanceResult | null;
  /** Battery capacity (kWh). */
  capacityKwh: number;
  /** Reserve threshold (kWh). */
  reserveThresholdKwh: number;
  /** Current SoC (0-100). */
  currentSocPct: number | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extract sunrise kWh values from forecast-runway chartData.
 * Returns null if data is invalid, stale, or sunrise index cannot be found.
 */
export function extractRunwaySunriseKwh(
  forecastData: ForecastData,
  capacityKwh: number,
): { expected: number; best: number; worst: number; confidence: 'low' | 'medium' | 'high' } | null {
  const cd = forecastData.chartData as {
    timestamps?: string[];
    socExpected?: number[];
    socBest?: number[];
    socWorst?: number[];
  } | undefined;
  if (!cd?.timestamps?.length || !cd.socExpected?.length) return null;

  const sunriseTime = String((forecastData as Record<string, unknown>).sunriseTime ?? '');
  if (!sunriseTime) return null;
  const sunriseTs = new Date(sunriseTime).getTime();
  if (isNaN(sunriseTs)) return null;

  const timestamps = cd.timestamps;
  let idx = timestamps.findIndex((ts) => new Date(ts).getTime() >= sunriseTs);
  if (idx < 0) {
    idx = timestamps.reduce((best, _ts, i) => {
      const t = new Date(timestamps[i]).getTime();
      const bestT = best >= 0 ? new Date(timestamps[best]).getTime() : Infinity;
      return Math.abs(t - sunriseTs) < Math.abs(bestT - sunriseTs) ? i : best;
    }, -1);
  }
  if (idx < 0) return null;

  // Determine if SoC is 0-1 decimal or 0-100 percent
  const socMax = cd.socExpected.length ? Math.max(...cd.socExpected) : 0;
  const isDecimal = socMax > 0 && socMax <= 1.5;
  const toKwh = (soc: number) => isDecimal ? soc * capacityKwh : (soc * capacityKwh) / 100;

  const expected = toKwh(cd.socExpected[idx] ?? 0);
  const best = toKwh(cd.socBest?.[idx] ?? 0);
  const worst = toKwh(cd.socWorst?.[idx] ?? 0);

  // Reject NaN/Infinity
  if (!Number.isFinite(expected) || !Number.isFinite(best) || !Number.isFinite(worst)) return null;

  const confidenceRaw = (forecastData as Record<string, unknown>).confidence as
    | { level?: string }
    | undefined;
  const confidence = (confidenceRaw?.level === 'high' || confidenceRaw?.level === 'medium' || confidenceRaw?.level === 'low')
    ? confidenceRaw.level
    : 'medium';

  return { expected, best, worst, confidence };
}

/**
 * Read the backend's absolute-kWh energy-position contract from a forecast-runway
 * result (Wave 1 / WT-145). Returns null when the backend hasn't provided these
 * fields (pre-deploy) so the caller can fall back to the SoC-based recompute.
 * These values are already computed against the authoritative plant capacity —
 * the frontend must NOT re-multiply them by any display capacity.
 */
export function readBackendAbsoluteKwh(
  forecastData: ForecastData,
): {
  expected: number;
  best: number;
  worst: number;
  currentEnergyKwh: number | null;
  reserveKwh: number | null;
  confidence: 'low' | 'medium' | 'high';
} | null {
  const r = forecastData as Record<string, unknown>;
  const exp = r.at_sunrise_expected_kwh;
  const best = r.at_sunrise_best_kwh;
  const worst = r.at_sunrise_worst_kwh;
  if (
    typeof exp !== 'number' || !Number.isFinite(exp) ||
    typeof best !== 'number' || !Number.isFinite(best) ||
    typeof worst !== 'number' || !Number.isFinite(worst)
  ) {
    return null;
  }
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  const confRaw = (r.confidence as { level?: string } | undefined)?.level;
  const confidence =
    confRaw === 'high' || confRaw === 'medium' || confRaw === 'low' ? confRaw : 'medium';
  return {
    expected: exp,
    best,
    worst,
    currentEnergyKwh: num(r.current_energy_kwh),
    reserveKwh: num(r.reserve_energy_kwh),
    confidence,
  };
}

// ─── Builder ─────────────────────────────────────────────────────────────────

/**
 * Build the canonical dashboard forecast view model.
 * Priority: forecast-runway (when loaded + valid) > overnight_forecasts table > unavailable.
 */
export function buildDashboardForecastViewModel(input: BuildForecastViewModelInput): DashboardForecastViewModel {
  const {
    forecastRunway,
    forecastRunwayLoading,
    forecastRunwayError,
    overnightForecast,
    capacityKwh,
    reserveThresholdKwh,
    currentSocPct,
  } = input;

  const currentEnergyKwh = currentSocPct != null && capacityKwh > 0
    ? (currentSocPct / 100) * capacityKwh
    : 0;

  // Attempt forecast-runway as primary source
  const runwayValid = !forecastRunwayLoading && !forecastRunwayError && forecastRunway != null;

  // PREFER the backend's absolute-kWh contract (Wave 1 / WT-145): forecast-runway
  // computes at-sunrise kWh from its OWN plant capacity, so the frontend does not
  // reconstruct SoC × display-capacity (which let a display/nameplate value leak
  // into runway math). Fall back to the SoC-based recompute only when the backend
  // hasn't been deployed with these fields yet (migration-safe).
  const backendAbs = runwayValid ? readBackendAbsoluteKwh(forecastRunway!) : null;
  if (backendAbs) {
    const { expected, best, worst, confidence } = backendAbs;
    const current = backendAbs.currentEnergyKwh ?? currentEnergyKwh;
    const reserve = backendAbs.reserveKwh ?? reserveThresholdKwh;
    return {
      source: 'forecast-runway',
      currentEnergyKwh: current,
      reserveThresholdKwh: reserve,
      expectedAtSunriseKwh: expected,
      bestAtSunriseKwh: best,
      worstAtSunriseKwh: worst,
      expectedUsableAtSunriseKwh: Math.max(0, expected - reserve),
      bestUsableAtSunriseKwh: Math.max(0, best - reserve),
      worstUsableAtSunriseKwh: Math.max(0, worst - reserve),
      reachesReserveBeforeSunrise: worst <= reserve,
      timeToReserve: null,
      confidence,
      isFresh: true,
    };
  }

  const runwaySunrise = runwayValid ? extractRunwaySunriseKwh(forecastRunway!, capacityKwh) : null;

  if (runwaySunrise) {
    const { expected, best, worst, confidence } = runwaySunrise;
    return {
      source: 'forecast-runway',
      currentEnergyKwh,
      reserveThresholdKwh,
      expectedAtSunriseKwh: expected,
      bestAtSunriseKwh: best,
      worstAtSunriseKwh: worst,
      expectedUsableAtSunriseKwh: Math.max(0, expected - reserveThresholdKwh),
      bestUsableAtSunriseKwh: Math.max(0, best - reserveThresholdKwh),
      worstUsableAtSunriseKwh: Math.max(0, worst - reserveThresholdKwh),
      reachesReserveBeforeSunrise: worst <= reserveThresholdKwh,
      timeToReserve: null, // derived elsewhere from depletionTime
      confidence,
      isFresh: true,
    };
  }

  // Fallback: overnight_forecasts table
  if (overnightForecast) {
    const expected = overnightForecast.expected_kwh;
    const lower = overnightForecast.lower_bound_kwh;
    const upper = overnightForecast.upper_bound_kwh;
    return {
      source: 'overnight_forecasts',
      currentEnergyKwh,
      reserveThresholdKwh,
      expectedAtSunriseKwh: expected,
      bestAtSunriseKwh: upper,
      worstAtSunriseKwh: lower,
      expectedUsableAtSunriseKwh: Math.max(0, expected - reserveThresholdKwh),
      bestUsableAtSunriseKwh: Math.max(0, upper - reserveThresholdKwh),
      worstUsableAtSunriseKwh: Math.max(0, lower - reserveThresholdKwh),
      reachesReserveBeforeSunrise: lower <= reserveThresholdKwh,
      timeToReserve: null,
      confidence: overnightForecast.confidence,
      isFresh: true,
    };
  }

  // Unavailable
  return {
    source: 'unavailable',
    currentEnergyKwh,
    reserveThresholdKwh,
    expectedAtSunriseKwh: 0,
    bestAtSunriseKwh: 0,
    worstAtSunriseKwh: 0,
    expectedUsableAtSunriseKwh: 0,
    bestUsableAtSunriseKwh: 0,
    worstUsableAtSunriseKwh: 0,
    reachesReserveBeforeSunrise: false,
    timeToReserve: null,
    confidence: 'low',
    isFresh: false,
  };
}

// ─── Dev logging ─────────────────────────────────────────────────────────────

/**
 * Log forecast view model state for debugging.
 * Call once per render cycle from PropertyDashboard.
 */
export function logForecastViewModel(
  vm: DashboardForecastViewModel,
  context: {
    energyOutlookRendered?: { expected: number; best: number; worst: number };
    overnightCardRendered?: { expected: number; lower: number; upper: number };
    guidanceDecision?: { state: string; forecastLowerKwh: number | null; reserveKwh: number | null };
  },
): void {
  if (process.env.NODE_ENV !== 'development') return;
  console.group('[forecast-vm] Dashboard Forecast View Model');
  console.log('source:', vm.source);
  console.log('expected/best/worst at sunrise (total):', vm.expectedAtSunriseKwh.toFixed(1), vm.bestAtSunriseKwh.toFixed(1), vm.worstAtSunriseKwh.toFixed(1));
  console.log('expected/best/worst usable (above reserve):', vm.expectedUsableAtSunriseKwh.toFixed(1), vm.bestUsableAtSunriseKwh.toFixed(1), vm.worstUsableAtSunriseKwh.toFixed(1));
  console.log('reserve threshold:', vm.reserveThresholdKwh.toFixed(1));
  console.log('reaches reserve before sunrise:', vm.reachesReserveBeforeSunrise);
  console.log('confidence:', vm.confidence);
  if (context.energyOutlookRendered) {
    console.log('EnergyOutlook rendered:', context.energyOutlookRendered);
  }
  if (context.overnightCardRendered) {
    console.log('OvernightForecastCard rendered:', context.overnightCardRendered);
  }
  if (context.guidanceDecision) {
    console.log('EnergyGuidance decision:', context.guidanceDecision);
  }
  console.groupEnd();
}
